import * as React from "react";
import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@material-ui/core";
import { BeaconWrapUpStory } from "../../../api/graphQL/queryModels";
import { useQuery, useMutation } from "@apollo/client";
import { GET_BEACON_WRAP_UP_STORY } from "../../../api/graphQL/queries";
import { useUserContext } from "../auth/UserContextProvider";
import { Link as RouterLink, useParams } from "react-router-dom";
import { BeaconWrapUpStoryInput } from "../../../api/graphQL/mutationModels";
import { useNotifications } from "../notifications/NotificationContextProvider";
import { UPSERT_BEACON_WRAP_UP_STORY } from "../../../api/graphQL/mutations";

interface WrapUpStoryArchiveProps {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const WrapUpStoryArchive = (props: WrapUpStoryArchiveProps) => {
    const { isOpen, setIsOpen } = props;
    const { setNotification } = useNotifications();
    const { user: currentUser, isContentAdmin } = useUserContext();
    const { id } = useParams<{ id: string }>();

    const { data } = useQuery<{ beaconWrapUpStory: BeaconWrapUpStory }>(GET_BEACON_WRAP_UP_STORY, {
        skip: !id,
        variables: {
            id
        }
    });

    const story = data?.beaconWrapUpStory;
    const archiveValue = !story?.isArchived;
    const canEdit = (!!currentUser?.id && story?.author?.id === currentUser.id) || isContentAdmin;

    const [upsertBeaconWrapUpStory] = useMutation<{ beaconWrapUpStoryUpsert: BeaconWrapUpStoryInput }>(UPSERT_BEACON_WRAP_UP_STORY);

    const handleSave = () => {
        setIsOpen(false);
        saveItem(storyInput);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const storyInput = story
        ? ({
              id: story.id,
              title: story.title,
              author: {
                  id: story.author?.id || currentUser?.id
              },
              dateCreated: story.dateCreated,
              datePublished: story.datePublished,
              isPublished: story.isPublished,
              isArchived: archiveValue,
              projectId: story.project?.id,
              description: story.description,
              images: story.images.map((i) => ({
                  title: i.title,
                  description: i.description,
                  filename: i.filename,
                  url: i.url,
                  optimizedFilename: i.optimizedFilename,
                  optimizedUrl: i.optimizedUrl,
                  thumbnailFilename: i.thumbnailFilename,
                  thumbnailUrl: i.thumbnailUrl,
                  showInGallery: i.showInGallery,
                  showInHeader: i.showInHeader
              })),
              previewDescription: story.previewDescription,
              selectedItems: story.selectedItems.map((i) => ({
                  title: i.title,
                  explanation: i.explanation,
                  selectedItemId: i.selectedItem.id,
                  outcome: {
                      justification: i.outcome.justification,
                      sentiment: i.outcome.sentiment
                  },
                  consideredAlternativeItemIds: i.consideredAlternativeItems.map((j) => j.id)
              }))
          } as BeaconWrapUpStoryInput)
        : null;

    const saveItem = async (beaconWrapUpStory: BeaconWrapUpStoryInput | null) => {
        story?.isArchived ? setNotification("Wrap-up story unarchived", "success") : setNotification("Wrap-up story archived", "success");

        await upsertBeaconWrapUpStory({
            variables: {
                beaconWrapUpStory
            }
        });
    };

    return (
        <div>
            <Dialog open={isOpen} aria-labelledby="alert-dialog-title">
                <DialogTitle id="dialog-title">{story?.isArchived ? "Unarchive" : "Archive"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="dialog-description">
                        {story?.isArchived
                            ? "Are you sure you want to unarchive this wrap-up story?"
                            : "Are you sure you want to archive this wrap-up story?"}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    {canEdit ? (
                        <Button onClick={handleClose} variant="outlined">
                            No, go back
                        </Button>
                    ) : null}
                    <Button onClick={handleSave} variant="contained" component={RouterLink} to={`/?view=stories`}>
                        {story?.isArchived ? "Yes, Unarchive" : "Yes, archive"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default WrapUpStoryArchive;
